import { type SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';

import { type EntityType, type Maybe, type ProductType } from '../../../../common/types';
import { type AggRequest, type AggResponse, fetchAgg } from '../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../utils/caching/in-memory';

const operationName = 'SearchEntitiesQuery';
const getSearchEntitiesQuery = ({
	includeSpaceKey,
	includeContainerId,
}: {
	includeSpaceKey?: boolean;
	includeContainerId?: boolean;
}) => {
	const SearchConfluenceSpace = `
    ... on SearchConfluenceSpace {
      key
    }
  `;

	// containerId is available on ExternalDocument entity (3P documents like Google Drive folders)
	const ContainerIdFragment = `
    ... on SearchResultGraphDocument {
      entity {
        ... on ExternalDocument {
          containerId
        }
      }
    }
  `;

	return `
  query ${operationName}(
		$experience: String!, 
		$query: String!, 
		$first: Int, 
		$filters: SearchFilterInput!
		$experimentId: String
		$shadowExperimentId: String
		$experimentLayers: [SearchExperimentLayer]
	) {
    search {
      results: search(
        experience: $experience
        query: $query
        first: $first
        filters: $filters
				experimentContext: {
					experimentId: $experimentId
					shadowExperimentId: $shadowExperimentId
					experimentLayers: $experimentLayers
				}
      ) {
        edges {
          node {
            id
            title
            type
            url
            iconUrl
            ${includeSpaceKey ? SearchConfluenceSpace : ``}
            ${includeContainerId ? ContainerIdFragment : ``}
          }
        }
      }
    }
  }
  `;
};

export type SearchEntitiesNode = {
	id: string;
	title: string;
	type: EntityType;
	url: string;
	iconUrl: Maybe<string>;
	key?: Maybe<string>;
	entity?: {
		containerId?: string;
	};
};

export type SearchEntitiesProductEntityType = `ati:cloud:${ProductType}:${EntityType}`;
export type SearchEntitiesProductARI = `ari:cloud:${ProductType}::${string}`;

type SearchEntitiesThirdPartyProductFilter = {
	integrationId: string;
	subtypes?: string[];
	product: string;
};

type SearchEntitiesThirdPartyFilters = {
	titleMatchOnly?: boolean;
	thirdPartyProducts?: SearchEntitiesThirdPartyProductFilter[];
	ancestorAris?: string[];
	containerAris?: string[];
};

export type SearchEntitiesQueryVariables = {
	query: string;
	experience?: string;
	first?: number;
	filters: {
		entities: SearchEntitiesProductEntityType[];
		locations: SearchEntitiesProductARI[];
		thirdPartyFilters?: SearchEntitiesThirdPartyFilters;
	};
	experimentId?: string;
	shadowExperimentId?: string;
	experimentLayers?: SearchExperimentLayer[];
};

export type SearchEntitiesQueryData = {
	search: Maybe<{
		results: {
			edges: Array<{
				node: SearchEntitiesNode;
			}>;
		};
	}>;
};

export const cache: {
	get: (key: string) => Promise<AggResponse<SearchEntitiesQueryData>> | undefined;
	set: (key: string, result: Promise<AggResponse<SearchEntitiesQueryData>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<SearchEntitiesQueryData>>,
		expireWhen?: ((result: AggResponse<SearchEntitiesQueryData>) => boolean) | undefined,
	) => Promise<AggResponse<SearchEntitiesQueryData>>;
} = inMemoryCache<AggResponse<SearchEntitiesQueryData>>();

// Expire the cache if there are errors or edges are empty
const expireWhen = (result: AggResponse<SearchEntitiesQueryData>) => {
	const { data, errors } = result;
	if (!data || (errors && errors.length > 0)) {
		return true;
	}
	return data.search?.results.edges.length === 0;
};

export type SearchEntitiesQueryProps = Pick<
	AggRequest<SearchEntitiesQueryVariables>,
	'aggAbsoluteUrl' | 'variables' | 'headers'
>;

export const searchEntitiesQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
	includeSpaceKey,
	includeContainerId,
}: SearchEntitiesQueryProps & {
	includeSpaceKey?: boolean;
	includeContainerId?: boolean;
}) => Promise<AggResponse<SearchEntitiesQueryData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
	includeSpaceKey,
	includeContainerId,
}: SearchEntitiesQueryProps & {
	includeSpaceKey?: boolean;
	includeContainerId?: boolean;
}) =>
	cache.inMemoryDecorator(
		generateHashFromVariables(variables),
		() =>
			fetchAgg<SearchEntitiesQueryVariables, SearchEntitiesQueryData>({
				variables: {
					first: 20,
					experience: '',
					...variables,
				},
				aggAbsoluteUrl,
				graphQuery: getSearchEntitiesQuery({ includeSpaceKey, includeContainerId }),
				operationName,
				headers,
			}),
		expireWhen,
	);
